import React, { useEffect, useImperativeHandle, forwardRef, useMemo } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';
import { xml } from '@codemirror/lang-xml';
import { html } from '@codemirror/lang-html';
import {
    search,
    searchKeymap,
    highlightSelectionMatches,
    SearchQuery,
    setSearchQuery,
    getSearchQuery,
    findNext,
    findPrevious,
    replaceNext,
    replaceAll,
    selectMatches,
    closeSearchPanel,
    openSearchPanel,
    searchPanelOpen
} from '@codemirror/search';
import { EditorState } from '@codemirror/state';
import { createTheme } from '@uiw/codemirror-themes';
import { tags as t } from '@lezer/highlight';
import { EditorView, keymap, runScopeHandlers } from '@codemirror/view';
import { foldCode, codeFolding, foldEffect, unfoldEffect } from '@codemirror/language';
import { syntaxTree } from '@codemirror/language';

const clickToFold = EditorView.domEventHandlers({
    mousedown: (event, view) => {
        // Get the clicked position
        const pos = view.posAtDOM(event.target);
        if (pos === null) return;

        // Check if the click is on a bracket/brace
        const line = view.state.doc.lineAt(pos);
        const char = line.text[pos - line.from];

        // Simple check for start brackets - robust check would involve syntax tree
        if (char === '{' || char === '[') {
            // Attempt to toggle fold at this position
            foldCode(view, { code: { from: pos, to: pos } });
            // We don't preventDefault to allow cursor placement, 
            // but if fold happens, visual change is enough.
        }
    }
});

const oneDarkTheme = createTheme({
    theme: 'dark',
    settings: {
        background: '#1e2227',
        foreground: '#abb2bf',
        caret: '#528bff',
        selection: '#3e4451',
        selectionMatch: '#3e4451',
        lineHighlight: 'rgba(255, 255, 255, 0.03)',
        gutterBackground: '#1e2227',
        gutterForeground: '#5c6370',
    },
    styles: [
        { tag: t.comment, color: '#5c6370', fontStyle: 'italic' },
        { tag: t.variableName, color: '#e06c75' },
        { tag: [t.string, t.special(t.brace)], color: '#98c379' },
        { tag: t.number, color: '#d19a66' },
        { tag: t.bool, color: '#c678dd' },
        { tag: t.null, color: '#56b6c2' },
        { tag: t.keyword, color: '#c678dd' },
        { tag: t.operator, color: '#56b6c2' },
        { tag: t.className, color: '#e5c07b' },
        { tag: t.definition(t.typeName), color: '#e5c07b' },
        { tag: t.typeName, color: '#e5c07b' },
        { tag: t.angleBracket, color: '#5c6370' },
        { tag: t.tagName, color: '#e06c75' },
        { tag: t.attributeName, color: '#d19a66' },
        { tag: t.propertyName, color: '#61afef' }, // JSON Keys
    ],
});

// 辅助函数：查找节点内的第一个括号位置
const findBracketPosition = (doc, from, to) => {
    for (let i = from; i < Math.min(from + 20, to); i++) {
        const char = doc.sliceString(i, i + 1);
        if (char === '{' || char === '[') {
            return i;
        }
    }
    return from;
};

// 辅助函数：计算折叠范围（排除空白字符）
const calculateFoldRange = (startLine, endLine) => {
    let foldFrom = startLine.to;
    let foldTo = endLine.from;
    
    // 找到第一行最后一个非空白字符的位置
    const startLineText = startLine.text;
    let lastNonWhitespace = startLineText.length;
    for (let i = startLineText.length - 1; i >= 0; i--) {
        if (startLineText[i] !== ' ' && startLineText[i] !== '\t') {
            lastNonWhitespace = i + 1;
            break;
        }
    }
    foldFrom = startLine.from + lastNonWhitespace;
    
    // 找到最后一行第一个非空白字符的位置
    const endLineText = endLine.text;
    let firstNonWhitespace = 0;
    for (let i = 0; i < endLineText.length; i++) {
        if (endLineText[i] !== ' ' && endLineText[i] !== '\t') {
            firstNonWhitespace = i;
            break;
        }
    }
    foldTo = endLine.from + firstNonWhitespace;
    
    return foldFrom < foldTo ? { from: foldFrom, to: foldTo } : null;
};

// 辅助函数：收集所有可折叠的节点
const collectFoldableNodes = (tree) => {
    const nodes = [];
    tree.iterate({
        enter: (node) => {
            if (!node.type) return;
            const nodeType = node.type.name;
            if (nodeType === 'Object' || nodeType === 'Array') {
                const from = node.from;
                const to = node.to;
                if (from >= to || (to - from) <= 2) return;
                nodes.push({ from, to, size: to - from });
            }
        }
    });
    return nodes;
};

const searchPhrases = {
    en: {
        Find: 'Find',
        Replace: 'Replace',
        next: 'Next',
        previous: 'Prev',
        all: 'All',
        'match case': 'Case',
        regexp: 'Regex',
        'by word': 'Whole word',
        replace: 'Replace',
        'replace all': 'Replace all',
        close: 'Close',
        'current match': 'Current match',
        'on line': 'on line',
        'replaced match on line $': 'Replaced match on line $',
        'replaced $ matches': 'Replaced $ matches'
    },
    zh: {
        Find: '查找',
        Replace: '替换',
        next: '下一个',
        previous: '上一个',
        all: '全部',
        'match case': '区分大小写',
        regexp: '正则',
        'by word': '全词匹配',
        replace: '替换',
        'replace all': '全部替换',
        close: '关闭',
        'current match': '当前匹配',
        'on line': '第',
        'replaced match on line $': '已替换第 $ 行的匹配',
        'replaced $ matches': '已替换 $ 处匹配'
    }
};

const toggleSearchPanel = (view) => {
    if (searchPanelOpen(view.state)) {
        return closeSearchPanel(view);
    }
    return openSearchPanel(view);
};

class FastSearchPanel {
    constructor(view, labels) {
        this.view = view;
        this.labels = labels;
        this.query = getSearchQuery(view.state);
        this.commit = this.commit.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);

        this.searchField = this.createTextInput('search', labels.Find, true);
        this.replaceField = this.createTextInput('replace', labels.Replace);
        this.caseField = this.createCheckbox('case', labels['match case']);
        this.reField = this.createCheckbox('re', labels.regexp);
        this.wordField = this.createCheckbox('word', labels['by word']);

        this.dom = document.createElement('div');
        this.dom.className = 'cm-search fd-search-panel';
        this.dom.addEventListener('keydown', this.handleKeydown);

        const rowPrimary = document.createElement('div');
        rowPrimary.className = 'fd-search-row fd-search-row-primary';
        rowPrimary.append(
            this.searchField,
            this.createButton('next', labels.next, () => findNext(view)),
            this.createButton('prev', labels.previous, () => findPrevious(view)),
            this.createButton('select', labels.all, () => selectMatches(view)),
            this.caseField.wrapper,
            this.reField.wrapper,
            this.wordField.wrapper,
            this.createIconButton(labels.close, () => closeSearchPanel(view))
        );

        const rowReplace = document.createElement('div');
        rowReplace.className = 'fd-search-row fd-search-row-replace';

        if (!view.state.readOnly) {
            rowReplace.append(
                this.replaceField,
                this.createButton('replace', labels.replace, () => replaceNext(view)),
                this.createButton('replaceAll', labels['replace all'], () => replaceAll(view))
            );
        }

        this.dom.append(rowPrimary);
        if (!view.state.readOnly) {
            this.dom.append(rowReplace);
        }

        this.setQuery(this.query);
    }

    createTextInput(name, placeholder, isMainField = false) {
        const input = document.createElement('input');
        input.type = 'text';
        input.name = name;
        input.placeholder = placeholder;
        input.setAttribute('aria-label', placeholder);
        if (isMainField) {
            input.setAttribute('main-field', 'true');
        }
        input.className = `fd-search-input fd-search-input-${name}`;
        input.addEventListener('input', this.commit);
        return input;
    }

    createCheckbox(name, labelText) {
        const wrapper = document.createElement('label');
        wrapper.className = 'fd-search-toggle';

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.name = name;
        input.addEventListener('change', this.commit);

        const text = document.createElement('span');
        text.textContent = labelText;

        wrapper.append(input, text);
        return { wrapper, input };
    }

    createButton(name, text, onClick) {
        const button = document.createElement('button');
        button.type = 'button';
        button.name = name;
        button.className = 'fd-search-btn';
        button.textContent = text;
        button.addEventListener('click', onClick);
        return button;
    }

    createIconButton(label, onClick) {
        const button = document.createElement('button');
        button.type = 'button';
        button.name = 'close';
        button.className = 'fd-search-icon-btn';
        button.setAttribute('aria-label', label);
        button.textContent = '×';
        button.addEventListener('click', onClick);
        return button;
    }

    commit() {
        const query = new SearchQuery({
            search: this.searchField.value,
            caseSensitive: this.caseField.input.checked,
            regexp: this.reField.input.checked,
            wholeWord: this.wordField.input.checked,
            replace: this.replaceField?.value || ''
        });

        if (!query.eq(this.query)) {
            this.query = query;
            this.view.dispatch({ effects: setSearchQuery.of(query) });
        }
    }

    handleKeydown(event) {
        if (runScopeHandlers(this.view, event, 'search-panel')) {
            event.preventDefault();
            return;
        }

        if (event.key === 'Enter' && event.target === this.searchField) {
            event.preventDefault();
            (event.shiftKey ? findPrevious : findNext)(this.view);
        } else if (event.key === 'Enter' && event.target === this.replaceField) {
            event.preventDefault();
            replaceNext(this.view);
        }
    }

    update(update) {
        for (const tr of update.transactions) {
            for (const effect of tr.effects) {
                if (effect.is(setSearchQuery) && !effect.value.eq(this.query)) {
                    this.setQuery(effect.value);
                }
            }
        }
    }

    setQuery(query) {
        this.query = query;
        this.searchField.value = query.search;
        this.replaceField.value = query.replace;
        this.caseField.input.checked = query.caseSensitive;
        this.reField.input.checked = query.regexp;
        this.wordField.input.checked = query.wholeWord;
    }

    mount() {
        this.searchField.focus();
        this.searchField.select();
    }

    destroy() {
        this.dom.removeEventListener('keydown', this.handleKeydown);
    }

    get pos() { return 80; }
    get top() { return true; }
}

const SmartEditor = forwardRef(({ value, onChange, language = 'json', readOnly = false, uiLanguage = 'en' }, ref) => {
    const editorRef = React.useRef(null);
    const viewRef = React.useRef(null);
    const containerRef = React.useRef(null);

    useEffect(() => {
        const handleGlobalKeydown = (event) => {
            const isFindShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'f';
            if (!isFindShortcut || event.altKey) {
                return;
            }

            const view = viewRef.current || editorRef.current?.view;
            if (!view) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            toggleSearchPanel(view);
        };

        window.addEventListener('keydown', handleGlobalKeydown, true);
        return () => window.removeEventListener('keydown', handleGlobalKeydown, true);
    }, []);

    useImperativeHandle(ref, () => ({
        foldAll: () => {
            const view = viewRef.current || editorRef.current?.view;
            if (!view) {
                console.warn('Editor view not available');
                return;
            }

            try {
                const tree = syntaxTree(view.state);
                const nodes = collectFoldableNodes(tree);
                
                if (nodes.length === 0) return;
                
                // 从后往前折叠，避免位置偏移（先折叠内层节点，再折叠外层节点）
                nodes.sort((a, b) => b.from - a.from);
                
                // 逐个应用折叠效果，确保每次使用最新状态
                nodes.forEach(({ from, to }) => {
                    try {
                        const startLine = view.state.doc.lineAt(from);
                        const endLine = view.state.doc.lineAt(to);
                        
                        // 只有跨越多行的节点才能折叠
                        if (endLine.number <= startLine.number) return;
                        
                        const bracketPos = findBracketPosition(view.state.doc, from, to);
                        const foldRange = foldCode(view, { code: { from: bracketPos, to: bracketPos } });
                        
                        if (foldRange?.from !== undefined && foldRange?.to !== undefined) {
                            view.dispatch({ effects: [foldEffect.of(foldRange)] });
                        } else {
                            // 使用备选方案：计算折叠范围
                            const calculatedRange = calculateFoldRange(startLine, endLine);
                            if (calculatedRange) {
                                view.dispatch({ effects: [foldEffect.of(calculatedRange)] });
                            }
                        }
                    } catch (e) {
                        // 静默处理错误，避免影响其他节点的折叠
                    }
                });
            } catch (error) {
                console.error('Error in foldAll:', error);
            }
        },
        unfoldAll: () => {
            const view = viewRef.current || editorRef.current?.view;
            if (!view) {
                console.warn('Editor view not available');
                return;
            }

            try {
                const tree = syntaxTree(view.state);
                const nodes = collectFoldableNodes(tree);
                
                if (nodes.length === 0) return;
                
                // 从内到外展开（从小到大），先展开子节点
                nodes.sort((a, b) => a.size - b.size);
                
                // 逐个应用展开效果，使用与 foldAll 相同的逻辑
                nodes.forEach(({ from, to }) => {
                    try {
                        const startLine = view.state.doc.lineAt(from);
                        const endLine = view.state.doc.lineAt(to);
                        
                        // 只有跨越多行的节点才能展开
                        if (endLine.number <= startLine.number) return;
                        
                        const bracketPos = findBracketPosition(view.state.doc, from, to);
                        const foldRange = foldCode(view, { code: { from: bracketPos, to: bracketPos } });
                        
                        if (foldRange?.from !== undefined && foldRange?.to !== undefined) {
                            view.dispatch({ effects: [unfoldEffect.of({ from: foldRange.from, to: foldRange.to })] });
                        } else {
                            // 使用备选方案：计算折叠范围
                            const calculatedRange = calculateFoldRange(startLine, endLine);
                            if (calculatedRange) {
                                view.dispatch({ effects: [unfoldEffect.of(calculatedRange)] });
                            }
                        }
                    } catch (e) {
                        // 静默处理错误，避免影响其他节点的展开
                    }
                });
            } catch (error) {
                console.error('Error in unfoldAll:', error);
            }
        }
    }));

    const extensions = useMemo(() => {
        const phrases = searchPhrases[uiLanguage] || searchPhrases.en;
        // 始终启用自动换行，包括压缩后的内容
        const baseExtensions = [
            EditorState.phrases.of(phrases),
            codeFolding(),
            search({
                top: true,
                createPanel: (view) => new FastSearchPanel(view, phrases)
            }),
            highlightSelectionMatches(),
            keymap.of([{
                key: 'Mod-f',
                run: toggleSearchPanel,
                preventDefault: true,
                scope: 'editor search-panel'
            }]),
            keymap.of(searchKeymap),
            EditorView.lineWrapping,
            clickToFold
        ];
        switch (language) {
            case 'json': return [json(), ...baseExtensions];
            case 'xml': return [xml(), ...baseExtensions];
            case 'html': return [html(), ...baseExtensions];
            default: return baseExtensions;
        }
    }, [language, uiLanguage]);

    return (
        <div
            ref={containerRef}
            style={{ 
            height: '100%', 
            borderRadius: '0',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <CodeMirror
                ref={(editor) => {
                    editorRef.current = editor;
                    if (editor?.view) {
                        viewRef.current = editor.view;
                    }
                }}
                value={value}
                height="100%"
                theme={oneDarkTheme}
                extensions={extensions}
                onChange={onChange}
                readOnly={readOnly}
                basicSetup={{
                    foldGutter: true,
                    dropCursor: false,
                    allowMultipleSelections: false,
                    indentOnInput: false,
                    lineNumbers: true,
                    highlightActiveLineGutter: true,
                    highlightActiveLine: true,
                    bracketMatching: true,
                    closeBrackets: true,
                    autocompletion: true,
                    history: true,
                }}
                style={{ 
                    fontSize: '14px', 
                    fontFamily: '"JetBrains Mono", monospace', 
                    height: '100%',
                    padding: '0'
                }}
            />
        </div>
    );
});

SmartEditor.displayName = 'SmartEditor';

export default SmartEditor;
